import * as React from 'react';

import {CssVarsProvider, useColorScheme} from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListSubheader from '@mui/joy/ListSubheader';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListItemContent from '@mui/joy/ListItemContent';
import Divider from '@mui/joy/Divider';

import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PaidIcon from '@mui/icons-material/Paid';
import SellIcon from '@mui/icons-material/Sell';
import InventoryIcon from '@mui/icons-material/Inventory';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';

import Main from './components/Main';
import Layout from './components/Layout';
import useToken, { hasPermission } from './components/useToken';
import Login from './pages/login/Login';
import { useState } from 'react';

function ColorSchemeToggle() {
    const {mode, setMode} = useColorScheme();
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => { setMounted(true); }, []);
    if (!mounted) return <IconButton size="sm" variant="outlined" color="primary"/>;
    return (
        <IconButton
            id="toggle-mode"
            size="sm"
            variant="outlined"
            color="primary"
            onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
        >
            {mode === 'light' ? <DarkModeRoundedIcon/> : <LightModeRoundedIcon/>}
        </IconButton>
    );
}

function NavItem({ href, icon, label, sezione, addHref }: { href: string; icon: React.ReactNode; label: string; sezione?: string; addHref?: string }) {
    return (
        <ListItem>
            <ListItemButton component="a" href={href}>
                <ListItemDecorator sx={{color: 'neutral.500'}}>{icon}</ListItemDecorator>
                <ListItemContent>{label}</ListItemContent>
            </ListItemButton>
            {sezione && hasPermission(sezione, 'create') && (
                <IconButton size="sm" variant="outlined" color="neutral" component="a" href={addHref ?? `${href}/aggiungi`} sx={{ml: 0.5}}>
                    <AddIcon/>
                </IconButton>
            )}
        </ListItem>
    );
}

function TeamNav() {
    return (
        <List size="sm" sx={{'--List-item-radius': '8px', '--List-gap': '4px'}}>
            <ListItem nested>
                <ListSubheader>
                    Gestionale
                    <IconButton size="sm" variant="plain" color="primary" sx={{'--IconButton-size': '24px', ml: 'auto'}}>
                        <KeyboardArrowDownRoundedIcon fontSize="small"/>
                    </IconButton>
                </ListSubheader>
                <List aria-labelledby="nav-list-browse" sx={{'& .JoyListItemButton-root': {p: '8px'}}}>
                    {hasPermission('schede', 'view') && (
                        <NavItem href="/schede" icon={<AssignmentIndRoundedIcon fontSize="small"/>} label="Schede" sezione="schede"/>
                    )}
                    {hasPermission('autori', 'view') && (
                        <NavItem href="/autori" icon={<ColorLensIcon fontSize="small"/>} label="Autori" sezione="autori"/>
                    )}
                    {hasPermission('opere', 'view') && (
                        <NavItem href="/opere" icon={<PeopleRoundedIcon fontSize="small"/>} label="Opere" sezione="opere"/>
                    )}
                    {hasPermission('localizzazioni', 'view') && (
                        <NavItem href="/localizzazioni" icon={<LocationOnIcon fontSize="small"/>} label="Localizzazioni" sezione="localizzazioni" addHref="/localizzazione/aggiungi"/>
                    )}
                    {hasPermission('gruppi', 'view') && (
                        <NavItem href="/gruppi" icon={<LocationCityIcon fontSize="small"/>} label="Gruppi" sezione="gruppi"/>
                    )}
                    {hasPermission('vendita', 'view') && (
                        <NavItem href="/fatture/vendita" icon={<SellIcon fontSize="small"/>} label="Vendita" sezione="vendita"/>
                    )}
                    {hasPermission('acquisto', 'view') && (
                        <NavItem href="/fatture/acquisto" icon={<InventoryIcon fontSize="small"/>} label="Acquisto" sezione="acquisto"/>
                    )}
                    {hasPermission('clienti', 'view') && (
                        <NavItem href="/clienti" icon={<PersonIcon fontSize="small"/>} label="Clienti" sezione="clienti"/>
                    )}
                    {hasPermission('statistiche', 'view') && (
                        <NavItem href="/reports" icon={<PaidIcon fontSize="small"/>} label="Statistiche"/>
                    )}
                </List>
            </ListItem>

            {(hasPermission('utenti', 'view') || hasPermission('ruoli', 'view') || hasPermission('permessi', 'view') || hasPermission('log', 'view')) && (
                <ListItem nested sx={{mt: 1}}>
                    <Divider sx={{mb: 1}}/>
                    <ListSubheader>
                        Amministrazione
                        <IconButton size="sm" variant="plain" color="primary" sx={{'--IconButton-size': '24px', ml: 'auto'}}>
                            <KeyboardArrowDownRoundedIcon fontSize="small"/>
                        </IconButton>
                    </ListSubheader>
                    <List aria-labelledby="nav-list-admin" sx={{'& .JoyListItemButton-root': {p: '8px'}}}>
                        {hasPermission('utenti', 'view') && (
                            <NavItem href="/utenti" icon={<ManageAccountsIcon fontSize="small"/>} label="Utenti" sezione="utenti"/>
                        )}
                        {hasPermission('ruoli', 'view') && (
                            <NavItem href="/ruoli" icon={<AdminPanelSettingsIcon fontSize="small"/>} label="Ruoli" sezione="ruoli"/>
                        )}
                        {hasPermission('permessi', 'view') && (
                            <NavItem href="/permessi" icon={<SecurityIcon fontSize="small"/>} label="Permessi" sezione="permessi"/>
                        )}
                        {hasPermission('log', 'view') && (
                            <NavItem href="/log" icon={<HistoryIcon fontSize="small"/>} label="Log attività"/>
                        )}
                    </List>
                </ListItem>
            )}
        </List>
    );
}

const getToken = () => {
    const tokenString = sessionStorage.getItem('token');
    // @ts-ignore
    return JSON.parse(tokenString);
};

export default function App() {
    const { token, setToken } = useToken();
    const [tmp, setTmp] = React.useState<any>();
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    if (!getToken()) {
        // @ts-ignore
        return <Login setToken={setToken} setTmp={setTmp}/>;
    }

    function handleLogout() {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('permissions');
        sessionStorage.removeItem('userInfo');
        setToken({ token: '' } as any);
    }

    return (
        <CssVarsProvider disableTransitionOnChange>
            <CssBaseline/>
            {drawerOpen && (
                <Layout.SideDrawer onClose={() => setDrawerOpen(false)}>
                    <TeamNav/>
                </Layout.SideDrawer>
            )}
            <Layout.Root sx={{...(drawerOpen && {height: '100vh', overflow: 'hidden'})}}>
                <Layout.Header>
                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5}}>
                        <IconButton
                            variant="outlined"
                            size="sm"
                            onClick={() => setDrawerOpen(true)}
                            sx={{display: {sm: 'none'}}}
                        >
                            <MenuIcon/>
                        </IconButton>
                        <img src="/palma.svg" width="25"/>
                        <Typography component="h1" fontWeight="xl">Palma Arte</Typography>
                    </Box>

                    <Box sx={{display: 'flex', flexDirection: 'row', gap: 1.5}}>
                        {hasPermission('schede', 'create') && (
                            <IconButton size="sm" variant="outlined" color="primary" component="a" href="/scheda/aggiungi">
                                <AddIcon/>
                            </IconButton>
                        )}
                        <ColorSchemeToggle/>
                        <IconButton size="sm" variant="outlined" color="primary" onClick={handleLogout}>
                            <LogoutIcon/>
                        </IconButton>
                    </Box>
                </Layout.Header>
                <Layout.SideNav>
                    <TeamNav/>
                </Layout.SideNav>
                <Layout.Main>
                    <Main/>
                </Layout.Main>
            </Layout.Root>
        </CssVarsProvider>
    );
}
